import { useState, useCallback, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import GoogleIcon from "@/components/icons/GoogleIcon";
import { ArrowLeft, Loader2, Phone } from "lucide-react";
import { useAuth } from "@um/ui";
import { getPlatform } from "@um/ui";
import { Browser } from "@capacitor/browser";
import { toast } from "sonner";
import { logEvent, logException } from "@um/ui";
import { storeDeferredDeepLinkInFirebase } from "@/utils/deepLinkUtils";
import { Capacitor } from "@capacitor/core";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { sendConversionEvent } from "@/services/social";
import { auth } from "@um/ui";
import { getUrlWithAttribution } from "@um/ui/utm";
import { RecaptchaVerifier } from "firebase/auth";
import type { ConfirmationResult } from "firebase/auth";
import { TestLoginButton } from "@um/ui/components";

export type AuthProvider = "Google" | "Apple" | "Email" | "Anonymous" | "Phone";

interface AuthPanelProps {
  title?: string;
  description?: string;
  onAuthenticated?: () => void | Promise<void | string>;
  enableEmailAuth?: boolean;
  enablePhoneAuth?: boolean;
  enableCreateAnonymous?: boolean;
  enableAppDownloadPrompt?: boolean;
  dontCreateAccount?: boolean;
  onNotRegistered?: () => void;
  showLogo?: boolean;
  termsUrl?: string;
  privacyUrl?: string;
  postAuthRedirectUrl?: string;
  showTermsAndPrivacy?: boolean;
  policyLinksInNewTab?: boolean;
}

export function AuthPanel({
  title = "Access your support system",
  description = "Create or access your account.",
  onAuthenticated,
  enableEmailAuth = true,
  enablePhoneAuth = false,
  enableCreateAnonymous = false,
  enableAppDownloadPrompt = true,
  dontCreateAccount,
  onNotRegistered,
  showLogo = true,
  termsUrl = "https://uniqueminds.com/terms",
  privacyUrl = "https://uniqueminds.com/privacy",
  postAuthRedirectUrl,
  showTermsAndPrivacy = true,
  policyLinksInNewTab = false,
}: AuthPanelProps) {
  const {
    user,
    userData,
    signInWithGoogle,
    signInWithApple,
    signInWithEmail,
    signInAnonymously,
    sendPhoneVerificationCode,
    confirmPhoneCode,
    logout,
  } = useAuth();

  const [activeProvider, setActiveProvider] = useState<AuthProvider | null>(
    null
  );
  const [showAppDownloadPrompt, setShowAppDownloadPrompt] = useState(false);
  const [showEmailAuth, setShowEmailAuth] = useState(false);
  const [showPhoneAuth, setShowPhoneAuth] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [phoneStep, setPhoneStep] = useState<"number" | "code">("number");
  const [verificationCode, setVerificationCode] = useState("");
  const recaptchaContainerRef = useRef<HTMLDivElement>(null);
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);
  const confirmationResultRef = useRef<ConfirmationResult | null>(null);
  const wasPhoneLinkRef = useRef(false);
  const [isCompletingAuth, setIsCompletingAuth] = useState(false);
  const [isExecutingCallback, setIsExecutingCallback] = useState(false);
  const [callbackRedirectUrl, setCallbackRedirectUrl] = useState<string | null>(
    null
  );
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const phoneAuthFromUrl = ["1", "true", "yes"].includes(
    (searchParams.get("phoneAuth") ?? "").toLowerCase()
  );
  const effectiveEnablePhoneAuth = enablePhoneAuth || phoneAuthFromUrl;

  const callOnAuthenticated = useCallback(async () => {
    try {
      if (auth.currentUser?.isAnonymous === false) {
        // only send this event when the user links an account
        sendConversionEvent("um_sign_in");
      }

      setIsExecutingCallback(true);
      const result = await onAuthenticated?.();
      // If callback returns a URL, store it for later use
      if (typeof result === "string") {
        setCallbackRedirectUrl(result);
        return result;
      }
      return null;
    } catch (error) {
      logException(error);
      console.error("Error executing onAuthenticated callback:", error);
      toast.error("Failed to complete setup. Please try again.", {
        position: "top-center",
      });
      return null;
    } finally {
      setIsExecutingCallback(false);
    }
  }, [onAuthenticated]);

  const handleSignIn = useCallback(
    async (provider: Exclude<AuthProvider, "Email">) => {
      try {
        setActiveProvider(provider);

        let hasAccount: boolean;
        if (provider === "Google") {
          hasAccount = await signInWithGoogle(dontCreateAccount);
        } else if (provider === "Apple") {
          hasAccount = await signInWithApple(dontCreateAccount);
        } else if (provider === "Anonymous") {
          hasAccount = await signInAnonymously();
        } else {
          throw new Error(`Unknown provider: ${provider}`);
        }

        if (dontCreateAccount && hasAccount === false) {
          await logout();
          onNotRegistered?.();
          return;
        }

        await callOnAuthenticated();

        // Show app download prompt if on web
        if (!Capacitor.isNativePlatform() && enableAppDownloadPrompt) {
          setShowAppDownloadPrompt(true);
        }
      } catch (error: any) {
        // Don't show error for user-initiated cancellation
        if (error?.code === "auth/cancelled") {
          console.log(`[AuthPanel] ${provider} sign-in was cancelled`);
        } else {
          logException(error);
          console.error(`Error signing in with ${provider}:`, error);
          toast.error("Failed to sign in. Please try again.", {
            position: "top-center",
          });
        }
      } finally {
        setActiveProvider(null);
      }
    },
    [
      signInWithGoogle,
      signInWithApple,
      signInAnonymously,
      logout,
      dontCreateAccount,
      enableAppDownloadPrompt,
      callOnAuthenticated,
      onNotRegistered,
    ]
  );

  async function emailSignIn(email: string, password: string): Promise<void> {
    const hasAccount = await signInWithEmail(
      email,
      password,
      dontCreateAccount
    );

    if (dontCreateAccount && hasAccount === false) {
      await logout();
      onNotRegistered?.();
      return;
    }

    await callOnAuthenticated();

    // Show app download prompt if on web
    if (!Capacitor.isNativePlatform() && enableAppDownloadPrompt) {
      setShowAppDownloadPrompt(true);
    }
  }

  const handleEmailSignIn = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setEmailError("");

      if (!email || !password) {
        setEmailError("Please enter both email and password");
        return;
      }

      try {
        setActiveProvider("Email");
        await emailSignIn(email, password);
      } catch (error: any) {
        logException(error);
        console.error("Error signing in with email:", error);

        if (
          error.code === "auth/user-not-found" ||
          error.code === "auth/wrong-password"
        ) {
          setEmailError("Invalid email or password");
        } else if (error.code === "auth/invalid-email") {
          setEmailError("Invalid email format");
        } else if (error.code === "auth/too-many-requests") {
          setEmailError("Too many attempts. Please try again later");
        } else {
          setEmailError("Failed to sign in. Please try again");
        }
      } finally {
        setActiveProvider(null);
      }
    },
    [
      email,
      password,
      signInWithEmail,
      logout,
      dontCreateAccount,
      enableAppDownloadPrompt,
      callOnAuthenticated,
      onNotRegistered,
    ]
  );

  const handleDownloadApp = useCallback(async () => {
    // Priority: postAuthRedirectUrl || callbackRedirectUrl || current path
    const urlToStore =
      postAuthRedirectUrl || callbackRedirectUrl || window.location.pathname;

    // OneLink + UTMs from User (localStorage is cleared at account creation)
    await Browser.open({
      url: getUrlWithAttribution(
        "appNative",
        urlToStore,
        userData?.conversionData
      ),
    });
    sendConversionEvent("app_download_clicked");

    if (user?.uid) {
      try {
        await storeDeferredDeepLinkInFirebase(user.uid, urlToStore);
      } catch (error) {
        console.error("Failed to store deferred link for app download:", error);
      }
    }

    // Close the drawer (navigation already happened in callback)
    // setShowAppDownloadPrompt(false);
  }, [user, userData, callbackRedirectUrl, postAuthRedirectUrl]);

  const handleDoLater = async () => {
    try {
      setIsCompletingAuth(true);
      logEvent("app_download_declined", { action: "do_later" });

      // Store deferred deep link even if they decline download
      // Priority: postAuthRedirectUrl || callbackRedirectUrl || current path
      const urlToNavigateTo =
        postAuthRedirectUrl || callbackRedirectUrl || window.location.pathname;

      setShowAppDownloadPrompt(false);
      navigate(urlToNavigateTo);
    } catch (error) {
      logException(error);
      console.error("Error completing authentication:", error);
      toast.error("Failed to complete setup. Please try again.", {
        position: "top-center",
      });
    } finally {
      setIsCompletingAuth(false);
    }
  };

  const handleBackToSocial = () => {
    setShowEmailAuth(false);
    setEmail("");
    setPassword("");
    setEmailError("");
  };

  const handleBackFromPhone = () => {
    setShowPhoneAuth(false);
    setPhoneNumber("");
    setPhoneError("");
    setPhoneStep("number");
    setVerificationCode("");
    confirmationResultRef.current = null;
    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear();
      recaptchaVerifierRef.current = null;
    }
  };

  // Create RecaptchaVerifier when phone auth is shown (web only)
  useEffect(() => {
    if (
      !showPhoneAuth ||
      Capacitor.isNativePlatform() ||
      !recaptchaContainerRef.current
    ) {
      return;
    }
    if (recaptchaVerifierRef.current) return;
    try {
      recaptchaVerifierRef.current = new RecaptchaVerifier(
        auth,
        "recaptcha-phone-container",
        {
          size: "invisible",
          callback: () => {
            console.log("Recaptcha verified");
          },
        }
      );
    } catch (err) {
      logException(err);
      setPhoneError(
        "Could not load verification. Please refresh and try again."
      );
    }
    return () => {
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (_) {}
        recaptchaVerifierRef.current = null;
      }
    };
  }, [showPhoneAuth]);

  const normalizePhoneNumber = (raw: string): string => {
    const digits = raw.replace(/\D/g, "");
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
    return raw.startsWith("+") ? raw : `+${raw}`;
  };

  const handleSendPhoneCode = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setPhoneError("");
      const normalized = normalizePhoneNumber(phoneNumber);
      if (!normalized || normalized.length < 10) {
        setPhoneError("Please enter a valid phone number");
        return;
      }
      const verifier = recaptchaVerifierRef.current;
      if (!verifier) {
        setPhoneError("Verification not ready. Please refresh and try again.");
        return;
      }
      try {
        setActiveProvider("Phone");
        wasPhoneLinkRef.current = auth.currentUser?.isAnonymous ?? false;
        const result = await sendPhoneVerificationCode(normalized, verifier);
        confirmationResultRef.current = result;
        setPhoneStep("code");
      } catch (error: any) {
        logException(error);

        // 🔥 IMPORTANT: reset verifier on ANY failure
        if (recaptchaVerifierRef.current) {
          recaptchaVerifierRef.current.clear();
          recaptchaVerifierRef.current = null;
        }

        if (error?.code === "auth/invalid-phone-number") {
          setPhoneError("Invalid phone number");
        } else if (error?.code === "auth/too-many-requests") {
          setPhoneError("Too many attempts. Try again later.");
        } else if (error?.code === "auth/invalid-app-credential") {
          setPhoneError("Verification expired. Please try again.");
        } else {
          setPhoneError("Failed to send code. Please try again.");
        }
      } finally {
        setActiveProvider(null);
      }
    },
    [phoneNumber, sendPhoneVerificationCode]
  );

  const handleConfirmPhoneCode = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setPhoneError("");
      const result = confirmationResultRef.current;
      if (!result) {
        setPhoneError("Session expired. Please request a new code.");
        return;
      }
      if (!verificationCode.trim()) {
        setPhoneError("Please enter the verification code");
        return;
      }
      try {
        setActiveProvider("Phone");
        const hasAccount = await confirmPhoneCode(
          result,
          verificationCode.trim(),
          {
            dontCreateAccount,
            wasLink: wasPhoneLinkRef.current,
          }
        );
        if (dontCreateAccount && hasAccount === false) {
          await logout();
          onNotRegistered?.();
          return;
        }
        await callOnAuthenticated();
        if (!Capacitor.isNativePlatform() && enableAppDownloadPrompt) {
          setShowAppDownloadPrompt(true);
        }
        setShowPhoneAuth(false);
        setPhoneNumber("");
        setVerificationCode("");
        setPhoneStep("number");
        confirmationResultRef.current = null;
      } catch (error: any) {
        logException(error);
        if (error?.code === "auth/invalid-verification-code") {
          setPhoneError(
            "Invalid or expired code. Try again or request a new code."
          );
        } else {
          setPhoneError("Verification failed. Please try again.");
        }
      } finally {
        setActiveProvider(null);
      }
    },
    [
      verificationCode,
      confirmPhoneCode,
      dontCreateAccount,
      logout,
      onNotRegistered,
      callOnAuthenticated,
      enableAppDownloadPrompt,
    ]
  );

  const platform = getPlatform();

  return (
    <div className="mx-auto w-full max-w-sm">
      <div className="text-center relative">
        {(showEmailAuth || showPhoneAuth) && (
          <Button
            variant="ghost"
            size="sm"
            className="absolute left-4 top-0 h-8 w-8 p-0"
            onClick={() => {
              if (showAppDownloadPrompt) {
                setShowAppDownloadPrompt(false);
              } else if (showPhoneAuth) {
                if (phoneStep === "code") {
                  setPhoneStep("number");
                  setVerificationCode("");
                } else {
                  handleBackFromPhone();
                }
              } else {
                handleBackToSocial();
              }
            }}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        )}

        {showLogo && (
          <div className="flex justify-center mb-4">
            <img
              src="/assets/brain-flower-logo-words-wide.svg"
              alt="Unique Minds"
              className="h-12"
            />
          </div>
        )}

        <h2 className="text-2xl font-bold text-gray-700">
          {showAppDownloadPrompt
            ? "Download our app!"
            : showEmailAuth
              ? "Special Access"
              : showPhoneAuth
                ? "Sign in with phone"
                : title}
        </h2>
        {!enableCreateAnonymous && (
          <p className="text-muted-foreground px-8 mt-2 mb-4">
            {showAppDownloadPrompt
              ? "Your characters are ready to help - the app is where the magic happens."
              : showEmailAuth
                ? "Sign in with the provided credentials"
                : showPhoneAuth
                  ? phoneStep === "code"
                    ? "Enter the code we sent to your phone"
                    : "We'll send you a verification code"
                  : description}
          </p>
        )}
      </div>

      <div className="px-6 space-y-4 pb-12">
        {showAppDownloadPrompt ? (
          <div className="space-y-4">
            <div className="space-y-3 flex flex-col items-center">
              <button
                onClick={handleDownloadApp}
                className="block w-full max-w-[280px]"
                disabled={isCompletingAuth}
                aria-label="Get the app on the App Store or Google Play"
              >
                <img
                  src="/assets/combined-download.webp"
                  alt="Download on the App Store and Google Play"
                  className="w-full h-auto object-contain"
                />
              </button>
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground"
                onClick={handleDoLater}
                disabled={isCompletingAuth}
              >
                {isCompletingAuth ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  "Continue on web instead"
                )}
              </Button>
            </div>
          </div>
        ) : showPhoneAuth &&
          effectiveEnablePhoneAuth &&
          !Capacitor.isNativePlatform() ? (
          <div className="space-y-4">
            <div id="recaptcha-phone-container" ref={recaptchaContainerRef} />
            {phoneStep === "number" ? (
              <form onSubmit={handleSendPhoneCode} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+1 650 555 0123"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    disabled={!!activeProvider || isExecutingCallback}
                    className="bg-white/70 backdrop-blur-sm"
                  />
                </div>
                {phoneError && (
                  <p className="text-sm text-red-600">{phoneError}</p>
                )}
                <Button
                  type="submit"
                  className="w-full"
                  disabled={!!activeProvider || isExecutingCallback}
                >
                  {activeProvider === "Phone" ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Send verification code"
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleConfirmPhoneCode} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Verification code</Label>
                  <Input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    placeholder="123456"
                    value={verificationCode}
                    onChange={(e) =>
                      setVerificationCode(
                        e.target.value.replace(/\D/g, "").slice(0, 6)
                      )
                    }
                    disabled={!!activeProvider || isExecutingCallback}
                    className="bg-white/70 backdrop-blur-sm"
                  />
                </div>
                {phoneError && (
                  <p className="text-sm text-red-600">{phoneError}</p>
                )}
                <Button
                  type="submit"
                  className="w-full"
                  disabled={!!activeProvider || isExecutingCallback}
                >
                  {isExecutingCallback ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Setting up...
                    </>
                  ) : activeProvider === "Phone" ? (
                    "Verifying..."
                  ) : (
                    "Verify and sign in"
                  )}
                </Button>
              </form>
            )}
          </div>
        ) : showEmailAuth && enableEmailAuth ? (
          <form onSubmit={handleEmailSignIn} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter provided email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!!activeProvider || isExecutingCallback}
                className="bg-white/70 backdrop-blur-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter provided password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={!!activeProvider || isExecutingCallback}
                className="bg-white/70 backdrop-blur-sm"
              />
            </div>
            {emailError && <p className="text-sm text-red-600">{emailError}</p>}
            <Button
              type="submit"
              className="w-full"
              disabled={!!activeProvider || isExecutingCallback}
            >
              {isExecutingCallback ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting up...
                </>
              ) : activeProvider === "Email" ? (
                "Signing in..."
              ) : (
                "Sign In"
              )}
            </Button>
          </form>
        ) : (
          <>
            {enableCreateAnonymous && (
              <>
                <h1 className="text-center text-3xl  font-bold text-gray-700">
                  Welcome to Unique Minds
                </h1>
                <p className="text-muted-foreground text-center">
                  Your support crew starts here.
                  <br />
                  Let’s get you connected.
                </p>
                <Button
                  variant="default"
                  className="w-full h-11 bg-black text-white hover:bg-black/90 flex items-center justify-center gap-0"
                  onClick={() => handleSignIn("Anonymous")}
                  disabled={!!activeProvider || isExecutingCallback}
                >
                  {isExecutingCallback ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Setting up...
                    </>
                  ) : (
                    <>
                      {activeProvider === "Anonymous"
                        ? "Processing..."
                        : "Explore Unique Minds"}
                    </>
                  )}
                </Button>

                <div className="flex items-center w-full">
                  <div className="flex-grow h-px bg-gray-200" />
                  <span className="mx-3 text-xs text-gray-400">or</span>
                  <div className="flex-grow h-px bg-gray-200" />
                </div>

                <p className="text-gray-700 text-center mt-2 font-medium">
                  Sign in to your Unique Minds account
                </p>
                <p className="text-muted-foreground text-center text-sm italic">
                  Includes accounts from Understand by Unique Minds
                </p>
              </>
            )}

            <Button
              variant="outline"
              className="w-full h-11 bg-white/70 backdrop-blur-sm hover:bg-white/90 flex items-center justify-center"
              onClick={() => handleSignIn("Google")}
              disabled={!!activeProvider || isExecutingCallback}
            >
              {isExecutingCallback ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting up...
                </>
              ) : (
                <>
                  <GoogleIcon className="mr-2 h-5 w-5" />
                  {activeProvider === "Google"
                    ? "Processing..."
                    : "Sign in with Google"}
                </>
              )}
            </Button>

            {platform !== "android" && (
              <Button
                variant="outline"
                className="w-full h-11 bg-white/70 backdrop-blur-sm hover:bg-white/90 flex items-center justify-center"
                onClick={() => handleSignIn("Apple")}
                disabled={!!activeProvider || isExecutingCallback}
              >
                {isExecutingCallback ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  <>
                    <img
                      src="/assets/apple-logo-light.svg"
                      alt=""
                      width={20}
                      height={20}
                    />
                    {activeProvider === "Apple"
                      ? "Processing..."
                      : "Sign in with Apple"}
                  </>
                )}
              </Button>
            )}

            {effectiveEnablePhoneAuth && !Capacitor.isNativePlatform() && (
              <Button
                variant="outline"
                className="w-full h-11 bg-white/70 backdrop-blur-sm hover:bg-white/90 flex items-center justify-center"
                onClick={() => setShowPhoneAuth(true)}
                disabled={!!activeProvider || isExecutingCallback}
              >
                <Phone className="mr-2 h-5 w-5" />
                Sign in with phone
              </Button>
            )}

            {enableEmailAuth && (
              <div className="text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setShowEmailAuth(true)}
                  disabled={!!activeProvider || isExecutingCallback}
                >
                  I have a code
                </Button>
              </div>
            )}

            <TestLoginButton onSignIn={emailSignIn} dontCreateAccount />
          </>
        )}

        {showTermsAndPrivacy && !showAppDownloadPrompt && (
          <p className="text-xs text-muted-foreground text-center px-2">
            By continuing, you agree to our{" "}
            <Link
              to={termsUrl}
              target={policyLinksInNewTab ? "_blank" : undefined}
              rel={policyLinksInNewTab ? "noopener noreferrer" : undefined}
              className="underline text-blue-600 hover:text-blue-800"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              to={privacyUrl}
              target={policyLinksInNewTab ? "_blank" : undefined}
              rel={policyLinksInNewTab ? "noopener noreferrer" : undefined}
              className="underline text-blue-600 hover:text-blue-800"
            >
              Privacy Policy
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}

export default AuthPanel;
